<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header hris-page-header-row">
        <div>
          <span class="hris-page-eyebrow">Human Resources</span>
          <h1 class="hris-page-title">Document Management</h1>
          <p class="hris-page-subtitle">
            {{ isAdmin ? "Browse, upload, and manage all company documents." : "Browse company policies and manage your own documents." }}
          </p>
        </div>
        <div class="row q-gutter-sm">
          <q-btn
            unelevated
            class="hris-q-primary"
            icon="fas fa-upload"
            label="Upload Document"
            @click="openUploadDialog"
          />
          <q-btn
            unelevated
            class="hris-q-outline"
            icon="fas fa-sync"
            label="Refresh"
            @click="getDocuments"
          />
        </div>
      </div>

      <div class="hris-panel" style="padding: 0; margin-bottom: 20px; overflow: hidden;">
        <q-tabs
          v-model="tab"
          active-color="green"
          indicator-color="green"
          align="left"
          inline-label
          dense
        >
          <q-tab name="all" label="All Documents" icon="fas fa-folder-open" />
          <q-tab
            v-for="cat in categories"
            :key="cat.value"
            :name="cat.value"
            :label="cat.label"
            :icon="cat.icon"
          />
        </q-tabs>
      </div>

      <div class="hris-toolbar">
        <div class="hris-toolbar-spacer"></div>
        <q-input
          v-model="searchFilter"
          placeholder="Search documents..."
          dense
          outlined
          color="green"
          style="min-width: 280px"
        >
          <template v-slot:append>
            <i class="fas fa-search"></i>
          </template>
        </q-input>
      </div>

      <div class="hris-panel">
        <q-table
          flat
          :data="filteredDocuments"
          :columns="columns"
          row-key="id"
          :filter="searchFilter"
          :loading="loading"
          separator="cell"
        >
          <template v-slot:loading>
            <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
              <q-spinner color="green" size="40px" />
              <div>Loading documents...</div>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="hris-empty full-width">No documents found.</div>
          </template>
          <template v-slot:body="props">
            <q-tr :props="props">
              <q-td key="title" :props="props">
                <div class="text-weight-bold">{{ props.row.title }}</div>
                <div class="text-caption text-grey-7">v{{ props.row.version }}</div>
              </q-td>
              <q-td key="category" :props="props">
                <q-chip dense color="blue-1" text-color="blue-9">
                  {{ categoryLabel(props.row.category) }}
                </q-chip>
              </q-td>
              <q-td key="uploader" :props="props">
                {{ props.row.uploader ? props.row.uploader.firstname + ' ' + props.row.uploader.lastname : 'N/A' }}
              </q-td>
              <q-td key="file_size" :props="props">{{ formatSize(props.row.file_size) }}</q-td>
              <q-td key="visibility" :props="props">
                <q-badge :class="props.row.is_private ? 'bg-grey text-white' : 'bg-green text-white'" class="q-pa-xs">
                  {{ props.row.is_private ? 'PRIVATE' : 'PUBLIC' }}
                </q-badge>
              </q-td>
              <q-td key="created_at" :props="props">{{ formatDate(props.row.created_at) }}</q-td>
              <q-td key="actions" :props="props" class="text-center">
                <q-btn
                  flat
                  round
                  dense
                  class="hris-q-ghost"
                  icon="fas fa-download"
                  :loading="downloadingId === props.row.id"
                  @click="downloadDocument(props.row)"
                >
                  <q-tooltip>Download</q-tooltip>
                </q-btn>
                <template v-if="canManage(props.row)">
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-edit"
                    @click="openEditDialog(props.row)"
                  >
                    <q-tooltip>Edit</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-trash-alt"
                    @click="confirmDelete(props.row)"
                  >
                    <q-tooltip>Delete</q-tooltip>
                  </q-btn>
                </template>
              </q-td>
            </q-tr>
          </template>
        </q-table>
      </div>

      <!-- UPLOAD DIALOG -->
      <q-dialog v-model="uploadDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Upload Document</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveUpload">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="uploadForm.title"
                    label="Title"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Title is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="uploadForm.category"
                    label="Category"
                    :options="categories"
                    option-value="value"
                    option-label="label"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Category is required']"
                  ></q-select>
                </div>
              </div>
              <div v-if="isAdmin" class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-toggle
                    v-model="uploadForm.is_private"
                    label="Private (HR / owner only)"
                    color="green"
                  ></q-toggle>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-file
                    ref="fileUploader"
                    v-model="uploadFileInput"
                    label="Select File"
                    outlined
                    dense
                    color="green"
                    clearable
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/jpeg,image/png"
                    :rules="[fileSizeRule]"
                    :max-file-size="maxFileSize"
                  ></q-file>
                  <div class="text-caption text-grey q-mt-xs">Max file size: 10 MB.</div>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Upload" type="submit" :loading="uploading" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- EDIT DIALOG -->
      <q-dialog v-model="editDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Edit Document</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveEdit">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="editForm.title"
                    label="Title"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Title is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="editForm.category"
                    label="Category"
                    :options="categories"
                    option-value="value"
                    option-label="label"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Category is required']"
                  ></q-select>
                </div>
              </div>
              <div v-if="isAdmin" class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-toggle
                    v-model="editForm.is_private"
                    label="Private (HR / owner only)"
                    color="green"
                  ></q-toggle>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="savingEdit" />
            </div>
          </q-form>
        </div>
      </q-dialog>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { date } from "quasar";
import mixins from "../../mixins";

export default {
  mixins: [mixins],
  data() {
    return {
      tab: "all",
      documents: [],
      loading: false,
      searchFilter: "",
      downloadingId: null,

      uploadDialog: false,
      uploading: false,
      uploadFileInput: null,
      maxFileSize: 10 * 1024 * 1024,
      uploadForm: {
        title: "",
        category: null,
        is_private: true
      },

      editDialog: false,
      savingEdit: false,
      editForm: {
        id: null,
        title: "",
        category: null,
        is_private: true
      },

      categories: [
        { value: "contract", label: "Contracts", icon: "fas fa-file-contract" },
        { value: "policy", label: "Policies", icon: "fas fa-file-alt" },
        { value: "government_form", label: "Government Forms", icon: "fas fa-landmark" },
        { value: "evaluation", label: "Evaluations", icon: "fas fa-clipboard-check" },
        { value: "certificate", label: "Certificates", icon: "fas fa-certificate" },
        { value: "employee_file", label: "Employee Files", icon: "fas fa-folder" },
        { value: "other", label: "Other", icon: "fas fa-file" }
      ]
    };
  },
  computed: {
    isAdmin() {
      const roleId = Number(this.userData.role_id);
      return roleId === 0 || roleId === 1;
    },
    filteredDocuments() {
      if (this.tab === "all") {
        return this.documents;
      }
      return this.documents.filter((doc) => doc.category === this.tab);
    },
    columns() {
      return [
        { name: "title", align: "left", label: "Title", sortable: true },
        { name: "category", align: "left", label: "Category", sortable: true },
        { name: "uploader", align: "left", label: "Uploaded By", sortable: true },
        { name: "file_size", align: "center", label: "Size" },
        { name: "visibility", align: "center", label: "Visibility" },
        { name: "created_at", align: "center", label: "Uploaded On", sortable: true },
        { name: "actions", align: "center", label: "Actions" }
      ];
    }
  },
  created() {
    this.getDocuments();
  },
  methods: {
    formatDate(d) {
      if (!d) return "N/A";
      return date.formatDate(d, "MMMM DD, YYYY");
    },
    formatSize(bytes) {
      if (!bytes) return "N/A";
      const units = ["B", "KB", "MB", "GB"];
      let size = bytes;
      let unitIndex = 0;
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      return `${size.toFixed(1)} ${units[unitIndex]}`;
    },
    categoryLabel(value) {
      const found = this.categories.find((c) => c.value === value);
      return found ? found.label : value;
    },
    getHeaders() {
      return {
        headers: { Authorization: "Bearer " + this.token }
      };
    },
    canManage(row) {
      return this.isAdmin || Number(row.uploaded_by) === Number(this.userData.id);
    },
    fileSizeRule(val) {
      if (!val) return true;
      return val.size <= this.maxFileSize || "File must be smaller than 10 MB.";
    },
    getDocuments() {
      this.loading = true;
      axios
        .get(this.apiUrl + "/documents/browse", this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.documents = res.data.data;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // --- UPLOAD ---
    openUploadDialog() {
      this.uploadForm = {
        title: "",
        category: null,
        is_private: true
      };
      this.uploadFileInput = null;
      this.uploadDialog = true;
    },
    saveUpload() {
      if (!this.uploadFileInput) {
        this.$q.notify({ type: "negative", message: "Please select a file to upload." });
        return;
      }

      this.uploading = true;
      const formData = new FormData();
      formData.append("title", this.uploadForm.title);
      formData.append("category", this.uploadForm.category);
      if (this.isAdmin) {
        formData.append("is_private", this.uploadForm.is_private ? 1 : 0);
      }
      formData.append("file", this.uploadFileInput, this.uploadFileInput.name);

      axios
        .post(this.apiUrl + "/documents/upload", formData, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Document uploaded successfully." });
            this.uploadDialog = false;
            this.getDocuments();
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed to upload document." });
          }
        })
        .catch(() => {
          this.$q.notify({ type: "negative", message: "Error communicating with server." });
        })
        .finally(() => {
          this.uploading = false;
        });
    },

    // --- DOWNLOAD ---
    downloadDocument(row) {
      this.downloadingId = row.id;
      axios
        .get(this.apiUrl + "/documents/download/" + row.id, {
          headers: { Authorization: "Bearer " + this.token },
          responseType: "blob"
        })
        .then((res) => {
          const contentType = res.headers["content-type"] || row.mime_type || "application/octet-stream";
          const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
          const link = document.createElement("a");
          link.href = blobUrl;
          const disposition = res.headers["content-disposition"] || "";
          const match = disposition.match(/filename="?([^"]+)"?/);
          link.download = match ? match[1] : row.title;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(() => {
          this.$q.notify({ type: "negative", message: "Unable to download document." });
        })
        .finally(() => {
          this.downloadingId = null;
        });
    },

    // --- EDIT ---
    openEditDialog(row) {
      this.editForm = {
        id: row.id,
        title: row.title,
        category: row.category,
        is_private: !!row.is_private
      };
      this.editDialog = true;
    },
    saveEdit() {
      this.savingEdit = true;
      const payload = {
        id: this.editForm.id,
        title: this.editForm.title,
        category: this.editForm.category
      };
      if (this.isAdmin) {
        payload.is_private = this.editForm.is_private ? 1 : 0;
      }
      axios
        .post(this.apiUrl + "/documents/update", payload, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Document updated successfully." });
            this.editDialog = false;
            this.getDocuments();
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed to update document." });
          }
        })
        .finally(() => {
          this.savingEdit = false;
        });
    },

    // --- DELETE ---
    confirmDelete(row) {
      this.$q
        .dialog({
          title: "Confirm Deletion",
          message: `Are you sure you want to delete "${row.title}"?`,
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          axios
            .post(this.apiUrl + `/documents/delete/${row.id}`, {}, this.getHeaders())
            .then((res) => {
              if (res.data.status) {
                this.$q.notify({ type: "positive", message: "Document deleted." });
                this.getDocuments();
              }
            });
        });
    }
  }
};
</script>
